import { handleApiResponse } from '@src/api/apiClient/axiosClient';
import {
  chatApiClient,
  ChatCreateErrorResponse,
  ChatCreateRequest,
  ChatCreateResponse,
  ChatUpdateErrorResponse,
  ChatUpdateRequest,
  ChatUpdateResponse,
  DeleteFileErrorResponse,
  DeleteFileResponse,
  UploadFileErrorResponse,
  UploadFileResponse,
} from '@src/api/shared/community/chatApiClient';
import type { Attachment } from '@src/idb';
import { ERROR_MESSAGES } from '@src/utils/errorMessages';
import { useCallback, useState } from 'react';

export interface UseChatSendResult {
  isSending: boolean;
  createMessage(
    htmlContent: string,
    textContent: string,
    attachmentsList: Attachment[],
    isAnonymous: boolean,
  ): Promise<boolean>;
  updateMessage(
    htmlContent: string,
    textContent: string,
    attachmentsList: Attachment[],
    isAnonymous: boolean,
    chatId: number,
  ): Promise<boolean>;

  uploadAttachment(file: File): Promise<Attachment | null>;
  deleteAttachment(attachment: Attachment): Promise<void>;
}

export const enum SubmitMode {
  CREATE = 'create',
  UPDATE = 'update',
}

export function useChatSend(
  channelCode?: string,
  setErrorMessage?: (msg: string | null) => void,
  parentChatId?: number,
): UseChatSendResult {
  const [isSending, setIsSending] = useState<boolean>(false);
  const MAX_LENGTH = 5000;

  const resetErrorMessage = useCallback(() => {
    setErrorMessage?.(null);
  }, [setErrorMessage]);

  const submitMessage = useCallback(
    async (
      mode: SubmitMode,
      params: {
        htmlContent: string;
        textContent: string;
        attachmentsList: Attachment[];
        isAnonymous: boolean;
        chatId?: number;
      },
    ): Promise<boolean> => {
      const { htmlContent, textContent, attachmentsList, chatId, isAnonymous } = params;

      if (!channelCode) {
        setErrorMessage?.(ERROR_MESSAGES.UPLOAD.CHANNEL_REQUIRED);
        return false;
      }
      if (textContent.trim().length === 0 && attachmentsList.length === 0) {
        setErrorMessage?.(ERROR_MESSAGES.UPLOAD.MESSAGE_OR_FILE_REQUIRED);
        return false;
      }
      if (htmlContent.length > MAX_LENGTH) {
        setErrorMessage?.(ERROR_MESSAGES.UPLOAD.MAX_LENGTH_EXCEEDED);
        return false;
      }

      setIsSending(true);
      resetErrorMessage();

      try {
        const attachmentsListFiltered = attachmentsList
          .filter((item): item is Attachment & { chatAttachment: { id: number } } =>
            Boolean(item.chatAttachment),
          )
          .map((item) => ({ chat_attachment_id: item.chatAttachment.id }));

        let apiResult;

        if (mode === SubmitMode.CREATE) {
          const requestBody: ChatCreateRequest = {
            message: htmlContent,
            attachments: attachmentsListFiltered,
            is_anonymous: isAnonymous,
            ...(parentChatId && { parent_chat_id: parentChatId }),
          };
          apiResult = await chatApiClient.create(channelCode, requestBody);
          handleApiResponse<ChatCreateResponse, ChatCreateErrorResponse>(
            apiResult,
            () => {},
            () => {
              setErrorMessage?.(ERROR_MESSAGES.UPLOAD.SEND_FAILED);
            },
          );
          return apiResult.result === 'success';
        } else if (mode === SubmitMode.UPDATE && chatId) {
          const requestBody: ChatUpdateRequest = {
            message: htmlContent,
            attachments: attachmentsListFiltered,
            is_anonymous: isAnonymous,
          };
          apiResult = await chatApiClient.update(channelCode, chatId, requestBody);
          if (apiResult.result === 'notFoundError') {
            setErrorMessage?.(ERROR_MESSAGES.UPDATE.NOTFOUND);
            return false;
          }
          handleApiResponse<ChatUpdateResponse, ChatUpdateErrorResponse>(
            apiResult,
            () => {},
            () => {
              setErrorMessage?.(ERROR_MESSAGES.UPDATE.FAILED);
            },
          );
          return apiResult.result === 'success';
        }
        return false;
      } catch {
        setErrorMessage?.(ERROR_MESSAGES.UPLOAD.SEND_FAILED);
        return false;
      } finally {
        setIsSending(false);
      }
    },
    [channelCode, setErrorMessage, resetErrorMessage, parentChatId],
  );

  const createMessage = useCallback(
    async (
      htmlContent: string,
      textContent: string,
      attachmentsList: Attachment[],
      isAnonymous: boolean,
    ): Promise<boolean> => {
      return await submitMessage(SubmitMode.CREATE, {
        htmlContent,
        textContent,
        attachmentsList,
        isAnonymous,
      });
    },
    [submitMessage],
  );

  const updateMessage = useCallback(
    async (
      htmlContent: string,
      textContent: string,
      attachmentsList: Attachment[],
      isAnonymous: boolean,
      chatId: number,
    ): Promise<boolean> => {
      return await submitMessage(SubmitMode.UPDATE, {
        htmlContent,
        textContent,
        attachmentsList,
        isAnonymous,
        chatId,
      });
    },
    [submitMessage],
  );

  const uploadAttachment = useCallback(
    async (file: File): Promise<Attachment | null> => {
      if (!channelCode) {
        setErrorMessage?.(ERROR_MESSAGES.UPLOAD.CHANNEL_REQUIRED);
        return null;
      }

      setIsSending(true);
      resetErrorMessage();

      try {
        const apiResult = await chatApiClient.upload(channelCode, file);
        let createdAttachment: Attachment | null = null;

        handleApiResponse<UploadFileResponse, UploadFileErrorResponse>(
          apiResult,
          (rawData) => {
            const fileData = rawData as UploadFileResponse;
            createdAttachment = {
              name: fileData.file_name,
              type: file.type,
              size: file.size,
              blob: file,
              chatAttachment: {
                id: fileData.id,
                file_name: fileData.file_name,
                extension: fileData.extension,
                file_url: fileData.file_url,
                thumbnail_url: fileData.thumbnail_url,
              },
            };
          },
          () => {
            setErrorMessage?.(ERROR_MESSAGES.UPLOAD.SEND_FAILED);
          },
        );

        return createdAttachment;
      } catch {
        setErrorMessage?.(ERROR_MESSAGES.UPLOAD.SEND_FAILED);
        return null;
      } finally {
        setIsSending(false);
      }
    },
    [channelCode, setErrorMessage, resetErrorMessage],
  );

  const deleteAttachment = useCallback(
    async (attachment: Attachment): Promise<void> => {
      if (!channelCode || !attachment.chatAttachment) {
        setErrorMessage?.(ERROR_MESSAGES.UPLOAD.CHANNEL_REQUIRED);
        return;
      }

      setIsSending(true);
      resetErrorMessage();

      try {
        const deleteParams = { file_url: attachment.chatAttachment.file_url };
        const apiResult = await chatApiClient.deleteFile(channelCode, deleteParams);
        handleApiResponse<DeleteFileResponse, DeleteFileErrorResponse>(
          apiResult,
          () => {},
          () => {
            setErrorMessage?.(ERROR_MESSAGES.UPLOAD.SEND_FAILED);
          },
        );
      } catch {
        setErrorMessage?.(ERROR_MESSAGES.UPLOAD.SEND_FAILED);
      } finally {
        setIsSending(false);
      }
    },
    [channelCode, setErrorMessage, resetErrorMessage],
  );

  return {
    isSending,
    createMessage,
    updateMessage,
    uploadAttachment,
    deleteAttachment,
  };
}
